import axios from 'axios';
import {
  DashboardData,
  DashboardApiResponse,
  SiteDistribution,
  DashboardKeywordListResponse
} from '../models/DashboardModels';
import { setupApiInterceptors } from '../utils/apiInterceptor';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { colors } from '../styles/designTokens';
import { formatIsoDateOnlyDisplay } from '../utils/dateUtils';

/** 주기별 자동/수동 막대에 쓰이는 팔레트 (표시용, API 스펙과 무관) */
const SITE_DISTRIBUTION_CHART_PALETTE = [
  colors.primary[900],
  colors.primary[500],
  colors.success[600],
  colors.warning[600],
  colors.warning[400],
  colors.error[600],
] as const;

// 환경변수에서 API 기본 URL 가져오기
const API_BASE_URL = getApiBaseUrl();
const API_PREFIX = '/api'; // API 프록시 경로

// Axios 인스턴스 생성
const dashboardApi = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 공통 인터셉터 설정
setupApiInterceptors(dashboardApi);

// 대시보드 데이터 서비스 클래스
export class DashboardService {

  // 실제 API에서 대시보드 데이터를 가져오는 메서드
  static async getDashboardDataFromAPI(date?: string): Promise<DashboardData> {
    try {
      // 날짜 파라미터 설정 (기본값: 오늘 날짜)
      const queryDate = date || new Date().toISOString().split('T')[0]; // YYYY-MM-DD 형식
      const token = localStorage.getItem('access_token');

      console.log('대시보드 API 호출:', {
        url: `/dashboard/?q=${queryDate}`,
        token: token ? `${token.substring(0, 20)}...` : 'No token',
        date: queryDate
      });

      const response = await dashboardApi.get(`/dashboard/?q=${queryDate}`);

      console.log('대시보드 API 응답 성공:', response.data);

      // API 응답을 기존 DashboardData 형식으로 변환
      return this.transformApiResponseToDashboardData(response.data);
    } catch (error: any) {
      console.error('대시보드 데이터 조회 실패:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  // API 응답을 기존 DashboardData 형식으로 변환하는 메서드
  static transformApiResponseToDashboardData(apiData: DashboardApiResponse): DashboardData {
    // 자동/수동 키워드 주기별 집계를 차트 데이터로 변환
    const siteDistribution: SiteDistribution[] = [];
    const chartData: any[] = [];

    let totalAuto = 0;
    let totalManual = 0;

    // 주기별 데이터를 처리하여 auto와 manual 값들의 합계 계산
    Object.entries(apiData.자동_수동_키워드_주기별_집계).forEach(([period, data], index) => {
      totalAuto += data.auto;
      totalManual += data.manual;

      siteDistribution.push({
        site: `${period}분 (자동)`,
        count: data.auto,
        percentage: 0 /* unused */,
        color: SITE_DISTRIBUTION_CHART_PALETTE[index * 2 % SITE_DISTRIBUTION_CHART_PALETTE.length],
      });

      siteDistribution.push({
        site: `${period}분 (수동)`,
        count: data.manual,
        percentage: 0 /* unused */,
        color:
          SITE_DISTRIBUTION_CHART_PALETTE[(index * 2 + 1) % SITE_DISTRIBUTION_CHART_PALETTE.length],
      });
    });

    console.log('키워드 집계 계산 결과:', {
      totalAuto,
      totalManual,
      원본데이터: apiData.자동_수동_키워드_주기별_집계
    });

    // 차트 데이터 생성
    chartData.push(
      { name: '자동 키워드', value: totalAuto, color: colors.primary[900] },
      { name: '수동 키워드', value: totalManual, color: colors.primary[500] }
    );

    // 전체 키워드 수 계산
    const totalKeywords = totalAuto + totalManual;

    // 증감 계산
    const 결제금액증감 = apiData.전일결제금액 - apiData.전전일결제금액;

    // 주간(전주/당주) 일별 결제금액/포인트 차감합 (차트용)
    const rangeLabel =
      apiData.주간시작일자 && apiData.주간종료일자
        ? `${formatIsoDateOnlyDisplay(apiData.주간시작일자)} ~ ${formatIsoDateOnlyDisplay(
            apiData.주간종료일자
          )}`
        : '';

    const previousPayments = apiData.주간결제금액이전일별 ?? [];
    const currentPayments = apiData.주간결제금액당주일별 ?? [];
    const previousPointDeductions = apiData.주간포인트차감이전일별 ?? [];
    const currentPointDeductions = apiData.주간포인트차감당주일별 ?? [];
    const monthlyCumulativePayments = apiData.월누적결제금액 ?? 0;
    const monthlyCumulativePaymentsSeries = apiData.월누적결제금액당주일별 ?? [];

    return {
      stats: {
        기준일자: apiData.기준일자,
        현재고객수: apiData.현재고객수,
        금일가입자수: apiData.금일가입자수,
        활성화키워드수: apiData.활성화키워드수,
        비활성키워드수: apiData.비활성키워드수,
        활성화키워드수_자동: apiData.활성화키워드수_자동,
        활성화키워드수_수동: apiData.활성화키워드수_수동,
        비활성키워드수_자동: apiData.비활성키워드수_자동,
        비활성키워드수_수동: apiData.비활성키워드수_수동,
        전일결제금액: apiData.전일결제금액,
        전전일결제금액: apiData.전전일결제금액,
        // 계산된 필드들
        전일결제금액증감: 결제금액증감,
      },
      keywordStats: {
        자동: totalAuto,
        수동: totalManual,
        total: totalKeywords
      },
      siteDistribution,
      chartData,
      weeklyDeduction:
        rangeLabel && (previousPayments.length > 0 || currentPayments.length > 0)
          ? {
              rangeLabel,
              previousPayments,
              currentPayments,
              previousPointDeductions,
              currentPointDeductions,
              monthlyCumulativePayments,
              monthlyCumulativePaymentsSeries
            }
          : undefined
    };
  }

  /** 대시보드 키워드 현황 카드 연동 목록 */
  static async getDashboardKeywords(params: {
    status: 'active' | 'inactive';
    page?: number;
    limit?: number;
  }): Promise<DashboardKeywordListResponse> {
    const { status, page = 1, limit = 8 } = params;
    const query = new URLSearchParams({
      status,
      page: String(page),
      limit: String(limit)
    });
    const response = await dashboardApi.get<DashboardKeywordListResponse>(
      `/dashboard/keywords/?${query.toString()}`
    );
    return response.data;
  }





} 